// Centralised route table — every route lazy-loaded so we only ship the chunk
// the user actually needs. Slashes initial bundle size (massive LCP win).
import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";

// HomePage stays eager because `/` is by far the most-hit route and we want
// no Suspense flicker on the most important paint.
import HomePage from "../pages/HomePage";

const WorldCupPage = lazy(() => import("../pages/WorldCupPage"));
const IPLPage = lazy(() => import("../pages/IPLPage"));
const PredictionDetailPage = lazy(() => import("../pages/PredictionDetailPage"));
const BlogPage = lazy(() => import("../pages/BlogPage"));
const BlogPostPage = lazy(() => import("../pages/BlogPostPage"));
const BestBettingSites = lazy(() => import("../pages/BestBettingSites"));
const CasinoReviews = lazy(() => import("../pages/CasinoReviews"));
const AdvertiseWithUs = lazy(() => import("../pages/AdvertiseWithUs"));
const PartnerSignup = lazy(() => import("../pages/PartnerSignup"));
const TrackingDemo = lazy(() => import("../pages/TrackingDemo"));
const AdminAffiliatesPage = lazy(() => import("../pages/AdminAffiliatesPage"));
const MediaKit = lazy(() => import("../pages/MediaKit"));
const AnalyticsDashboard = lazy(() => import("../pages/AnalyticsDashboard"));
const PublicAnalyticsPage = lazy(() => import("../pages/PublicAnalyticsPage"));
const NewsPage = lazy(() => import("../pages/NewsPage"));
const NewsArticlePage = lazy(() => import("../pages/NewsArticlePage"));
const AdminNewsPage = lazy(() => import("../pages/AdminNewsPage"));

// Static content pages share one bundle — extracted once at module scope so
// React doesn't unmount/remount the lazy wrapper on every render.
const AboutPage = lazy(() =>
  import("../pages/StaticPages").then((m) => ({ default: m.AboutPage })),
);
const ContactPage = lazy(() =>
  import("../pages/StaticPages").then((m) => ({ default: m.ContactPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import("../pages/StaticPages").then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsPage = lazy(() =>
  import("../pages/StaticPages").then((m) => ({ default: m.TermsPage })),
);

import PasswordGate from "./auth/PasswordGate";

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center bg-slate-950">
    <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
  </div>
);

const AppRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/worldcup" element={<WorldCupPage />} />
      <Route path="/ipl" element={<IPLPage />} />
      <Route path="/predictions/:matchId" element={<PredictionDetailPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:slug" element={<NewsArticlePage />} />
      <Route path="/best-betting-sites" element={<BestBettingSites />} />
      <Route path="/casino-reviews" element={<CasinoReviews />} />
      <Route path="/advertise" element={<AdvertiseWithUs />} />
      <Route path="/partners" element={<PartnerSignup />} />
      <Route path="/tracking-demo" element={<TrackingDemo />} />
      <Route path="/media-kit" element={<MediaKit />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/analytics" element={<PublicAnalyticsPage />} />
      <Route
        path="/admin/analytics"
        element={<PasswordGate><AnalyticsDashboard /></PasswordGate>}
      />
      <Route
        path="/admin/affiliates"
        element={<PasswordGate><AdminAffiliatesPage /></PasswordGate>}
      />
      <Route
        path="/admin/news"
        element={<PasswordGate><AdminNewsPage /></PasswordGate>}
      />
    </Routes>
  </Suspense>
);

export default AppRoutes;
